import * as XLSX from "xlsx";
import {
  IMPORT_COLUMNS,
  MAX_IMPORT_ROWS,
  MAX_RANGE_DAYS,
  daysBetween,
  isBlank,
  normalizeCode,
  normalizeHeader,
  parseAmountCell,
  parseDateCell,
  planBdfExcelImport,
  readWorkbook,
  sheetToRows,
} from "./bdfExcelImport";

export const MEDIA_VEHICLES = ["Gantry", "Unipole", "Hoarding", "Cantilever", "LED", "Kiosk"];
export const ILLU_TYPES = ["FL", "BL", "NL"];
export const SUFFIXES = ["HO", "PRD", "PRN", "BR", "OTH"];
export const DEFAULT_SUFFIX = "HO";

const dateAliases = (key) => IMPORT_COLUMNS.find((c) => c.key === key).aliases;

export const TRADING_COLUMNS = [
  {
    key: "location",
    label: "Location",
    required: true,
    aliases: ["location", "locations", "locationname", "site", "sitelocation", "sitename"],
  },
  {
    key: "town",
    label: "Town",
    aliases: ["town", "city", "area", "townorcity", "towncity", "cityarea", "townarea", "towncityarea"],
  },
  {
    key: "mediaVehicle",
    label: "Media Vehicle",
    aliases: ["mediavehicle", "vehicle", "mediaformat", "typeofmedia", "mediavehicletype"],
  },
  {
    key: "width",
    label: "Size (W)",
    aliases: ["sizew", "w", "width", "sizewidth", "widthft", "wft"],
  },
  {
    key: "height",
    label: "Size (H)",
    aliases: ["sizeh", "h", "height", "sizeheight", "heightft", "hft"],
  },
  {
    key: "illu",
    label: "Media Type",
    aliases: ["mediatype", "illu", "illumination", "illutype", "lighttype", "lighting"],
  },
  {
    key: "ratePerMonth",
    label: "Rate PM",
    aliases: ["ratepm", "ratepermonth", "rpm", "cardrate", "cardratepm", "rackrate", "rackratepm"],
  },
  {
    key: "buyingRate",
    label: "Buying Rate PM",
    aliases: [
      "buyingratepm",
      "buyingrate",
      "buyingratepermonth",
      "buyingcost",
      "buyingcostpermonth",
      "purchaserate",
      "purchaseratepm",
      "purchasecost",
    ],
  },
  {
    key: "printingCharge",
    label: "Printing Charges",
    aliases: ["printingcharges", "printingcharge", "printcharges", "printcharge", "printing", "printingcost"],
  },
  {
    key: "mountingCharge",
    label: "Mounting Charges",
    aliases: ["mountingcharges", "mountingcharge", "mountcharges", "mountcharge", "mounting", "mountingcost"],
  },
  { key: "startDate", label: "Start Date", required: true, aliases: dateAliases("startDate") },
  { key: "endDate", label: "End Date", required: true, aliases: dateAliases("endDate") },
];

const FIELD_BY_KEY = Object.fromEntries(TRADING_COLUMNS.map((c) => [c.key, c]));

/** Same idea as normalizeCode: spaces, punctuation, case and look-alike characters are ignored. */
export const normalizeLocationKey = (value) =>
  String(value ?? "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");

const cleanText = (value) => String(value ?? "").replace(/\s+/g, " ").trim();

const matchTradingHeader = (header) => {
  const norm = normalizeHeader(header);
  if (!norm) return null;
  const exact = TRADING_COLUMNS.find((c) => c.aliases.includes(norm));
  if (exact) return exact.key;
  let best = null;
  TRADING_COLUMNS.forEach((c) => {
    c.aliases.forEach((alias) => {
      if (alias.length >= 6 && norm.startsWith(alias) && (!best || alias.length > best.len)) {
        best = { key: c.key, len: alias.length };
      }
    });
  });
  return best ? best.key : null;
};

const findTradingHeader = (rows) => {
  let best = null;
  const limit = Math.min(rows.length, 30);
  for (let r = 0; r < limit; r += 1) {
    const cells = rows[r] || [];
    const columns = {};
    const matched = [];
    const duplicates = [];
    const ignored = [];
    cells.forEach((cell, idx) => {
      if (isBlank(cell)) return;
      const text = cleanText(cell);
      const key = matchTradingHeader(cell);
      if (!key) {
        ignored.push({ header: text, col: idx });
      } else if (columns[key] !== undefined) {
        duplicates.push({ header: text, key });
      } else {
        columns[key] = idx;
        matched.push({ key, label: FIELD_BY_KEY[key].label, header: text });
      }
    });
    const score = matched.length;
    if (score > 0 && (!best || score > best.score)) {
      best = { index: r, columns, matched, duplicates, ignored, score };
    }
  }
  return best;
};

const missingRequired = (columns) =>
  TRADING_COLUMNS.filter((c) => c.required && columns[c.key] === undefined).map((c) => c.label);

export const parseTradingExcelBuffer = (buffer) => {
  let workbook;
  try {
    workbook = readWorkbook(buffer);
  } catch (e) {
    return { ok: false, error: "This file could not be read. Upload a .xlsx, .xls or .csv file." };
  }
  const sheetNames = workbook.SheetNames || [];
  if (sheetNames.length === 0) return { ok: false, error: "The file has no sheets." };

  const date1904 = Boolean(workbook.Workbook && workbook.Workbook.WBProps && workbook.Workbook.WBProps.date1904);

  let chosen = null;
  let fallback = null;
  for (const name of sheetNames) {
    const sheet = workbook.Sheets[name];
    if (!sheet) continue;
    const { rows, firstRow, hidden } = sheetToRows(sheet);
    const header = findTradingHeader(rows);
    if (!header) continue;
    const missing = missingRequired(header.columns);
    if (missing.length === 0) {
      chosen = { name, rows, firstRow, hidden, header };
      break;
    }
    if (!fallback || header.score > fallback.header.score) fallback = { header, missing };
  }

  if (!chosen) {
    if (fallback) {
      return {
        ok: false,
        error: `Missing required column${fallback.missing.length > 1 ? "s" : ""}: ${fallback.missing.join(", ")}.`,
        foundHeaders: fallback.header.matched.map((m) => m.header).concat(fallback.header.ignored.map((i) => i.header)),
      };
    }
    return { ok: false, error: "No header row found. The sheet needs columns for Location, Start Date and End Date." };
  }

  const { rows, firstRow, hidden, header } = chosen;
  const cols = header.columns;
  const dataRows = [];
  const skipped = [];
  const hiddenInData = [];

  for (let r = header.index + 1; r < rows.length; r += 1) {
    const cells = rows[r] || [];
    const get = (key) => (cols[key] === undefined ? undefined : cells[cols[key]]);
    const rowNumber = firstRow + r + 1;

    const locationCell = get("location");
    const startCell = get("startDate");
    const endCell = get("endDate");

    if (isBlank(locationCell) && isBlank(startCell) && isBlank(endCell)) {
      if (TRADING_COLUMNS.some((c) => !isBlank(get(c.key)))) skipped.push(rowNumber);
      continue;
    }

    const errors = [];
    const location = isBlank(locationCell) ? "" : cleanText(locationCell);
    if (!location) errors.push("Location is required");

    const start = parseDateCell(startCell, { date1904 });
    const end = parseDateCell(endCell, { date1904 });
    if (start.blank) errors.push("Start Date is required");
    else if (start.error) errors.push(`Start Date: ${start.error}`);
    if (end.blank) errors.push("End Date is required");
    else if (end.error) errors.push(`End Date: ${end.error}`);

    const amount = (key, label) => {
      if (cols[key] === undefined) return { blank: true };
      const parsed = parseAmountCell(get(key), label);
      if (parsed.error) errors.push(parsed.error);
      return parsed;
    };
    const width = amount("width", "Size (W)");
    const height = amount("height", "Size (H)");
    const ratePerMonth = amount("ratePerMonth", "Rate PM");
    const buyingRate = amount("buyingRate", "Buying Rate PM");
    const printing = amount("printingCharge", "Printing Charges");
    const mounting = amount("mountingCharge", "Mounting Charges");

    if (hidden.has(r)) hiddenInData.push(rowNumber);
    dataRows.push({
      rowNumber,
      location,
      town: isBlank(get("town")) ? "" : cleanText(get("town")),
      mediaVehicle: isBlank(get("mediaVehicle")) ? "" : cleanText(get("mediaVehicle")),
      illu: isBlank(get("illu")) ? "" : cleanText(get("illu")).toUpperCase(),
      width: width.value ?? null,
      height: height.value ?? null,
      ratePerMonth: ratePerMonth.value ?? null,
      specialRatePerMonth: null,
      buyingRate: buyingRate.value ?? null,
      printingCharge: printing.value ?? null,
      mountingCharge: mounting.value ?? null,
      startDate: start.value || null,
      endDate: end.value || null,
      errors,
    });
  }

  if (dataRows.length === 0) return { ok: false, error: "No inventory rows found below the header row." };
  if (dataRows.length > MAX_IMPORT_ROWS) {
    return {
      ok: false,
      error: `The file has ${dataRows.length} rows; at most ${MAX_IMPORT_ROWS} can be imported at once.`,
    };
  }

  const warnings = [];
  header.duplicates.forEach((d) =>
    warnings.push(
      `"${d.header}" appears more than once; the first "${FIELD_BY_KEY[d.key].label}" column is used and this one is ignored.`
    )
  );
  if (skipped.length > 0) {
    warnings.push(
      `${skipped.length} row${skipped.length > 1 ? "s" : ""} without Location or dates ${
        skipped.length > 1 ? "were" : "was"
      } skipped (row ${skipped.slice(0, 8).join(", ")}${skipped.length > 8 ? ", ..." : ""}).`
    );
  }
  if (hiddenInData.length > 0) {
    warnings.push(
      `${hiddenInData.length} hidden row${hiddenInData.length > 1 ? "s" : ""} in the sheet ${
        hiddenInData.length > 1 ? "are" : "is"
      } included (row ${hiddenInData.slice(0, 8).join(", ")}${hiddenInData.length > 8 ? ", ..." : ""}).`
    );
  }

  return {
    ok: true,
    sheetName: chosen.name,
    headerRowNumber: firstRow + header.index + 1,
    matched: header.matched,
    ignored: header.ignored,
    warnings,
    rows: dataRows,
    hasColumn: Object.fromEntries(TRADING_COLUMNS.map((c) => [c.key, cols[c.key] !== undefined])),
  };
};

const canonicalVehicle = (value) => {
  const key = normalizeLocationKey(value);
  return MEDIA_VEHICLES.find((v) => normalizeLocationKey(v) === key) || null;
};

/**
 * Finds the inventory a row refers to, by location (the town is only used to
 * tell apart two inventories that share a location name).
 *
 * `candidates` are API inventories: { locations: { name, city, Area }, isTrading, isArchived, CodeNo }.
 * Returns { match, kind: "trading" | "own" } | { ambiguous: true, matches } | null.
 */
export const findInventoryByLocation = (candidates, location, town) => {
  const key = normalizeLocationKey(location);
  if (!key) return null;
  let found = candidates.filter((c) => normalizeLocationKey(c?.locations?.name) === key);
  if (found.length === 0) return null;
  if (found.length > 1) {
    const townKey = normalizeLocationKey(town);
    const narrowed = townKey
      ? found.filter(
          (c) =>
            normalizeLocationKey(c?.locations?.city) === townKey ||
            normalizeLocationKey(c?.locations?.Area) === townKey
        )
      : [];
    if (narrowed.length === 1) {
      found = narrowed;
    } else {
      // Still more than one: if exactly one of them is a live trading inventory,
      // that is the one a trading sheet means.
      const pool = narrowed.length > 1 ? narrowed : found;
      const trading = pool.filter((c) => c.isTrading && !c.isArchived);
      if (trading.length !== 1) return { ambiguous: true, matches: pool };
      found = trading;
    }
  }
  const match = found[0];
  return { match, kind: match.isTrading && !match.isArchived ? "trading" : "own" };
};

const rangesOverlap = (aStart, aEnd, bStart, bEnd) => {
  const key = (d) => d.split("-").reverse().join("");
  return key(aStart) <= key(bEnd) && key(bStart) <= key(aEnd);
};

/**
 * Turns parsed rows into a plan. Rows whose location is already a trading
 * inventory are planned with the same rules as the Code No import; the others
 * are planned as new trading inventories (created when the user imports).
 *
 * ctx: {
 *   rows, candidates, existingInventories, allowDuplicateInventories, packageRate,
 *   mapTradingInv(apiInv) -> line base, createLine, isDateBlocked, hasSiblingOverlap,
 *   calcSpecialRate, canAddToCart
 * }
 */
export const planTradingExcelImport = ({
  rows,
  candidates,
  existingInventories,
  allowDuplicateInventories,
  packageRate,
  mapTradingInv,
  createLine,
  isDateBlocked,
  hasSiblingOverlap,
  calcSpecialRate,
  canAddToCart,
}) => {
  const byRow = new Map();
  const existingRows = [];
  const inventoryByCode = new Map();
  const groups = new Map(); // groupKey -> rows (new inventories only)

  rows.forEach((row) => {
    const errors = [...row.errors];
    const out = {
      rowNumber: row.rowNumber,
      code: "",
      location: row.location,
      town: row.town,
      kind: null,
      startDate: row.startDate,
      endDate: row.endDate,
      printingCharge: null,
      mountingCharge: null,
      specialRatePerMonth: null,
      amount: null,
      defaults: {},
      errors,
      warnings: [],
      line: null,
      status: "error",
    };
    byRow.set(row.rowNumber, out);
    if (row.location === "" || errors.length > 0) {
      // Base problems (missing location / bad dates or numbers) – nothing else to check.
      if (row.location) out.kind = null;
      return;
    }

    const found = findInventoryByLocation(candidates, row.location, row.town);
    if (found && found.ambiguous) {
      const list = (found.matches || [])
        .slice(0, 4)
        .map(
          (m) =>
            `${m.CodeNo || "no code"} (${m?.locations?.city || m?.locations?.Area || "-"}, ${
              m.isTrading ? "trading" : "own"
            }${m.isArchived ? ", removed" : ""})`
        )
        .join("; ");
      errors.push(
        `This location matches ${(found.matches || []).length} inventories: ${list}. Make the Town match one of them, or use the Code No upload.`
      );
      return;
    }
    if (found && found.kind === "own") {
      errors.push(
        `Location already exists as "${found.match.CodeNo}" (not a trading inventory). Use Upload Excel with its Code No.`
      );
      return;
    }

    if (found) {
      const inv = found.match;
      const base = mapTradingInv(inv);
      out.kind = "existing";
      out.code = base.codeNo || "";
      out.vendor = inv.ownedBy || inv.vendor || "";
      out.suffix = inv.suffix || "";
      const townKey = normalizeLocationKey(row.town);
      if (
        townKey &&
        normalizeLocationKey(inv?.locations?.city) !== townKey &&
        normalizeLocationKey(inv?.locations?.Area) !== townKey
      ) {
        out.warnings.push(
          `Matched existing inventory ${base.codeNo || out.location} in "${inv?.locations?.city || inv?.locations?.Area || "-"}" (file says "${row.town}")`
        );
      }
      // Looked up by id: trading inventories do not always have a Code No.
      inventoryByCode.set(normalizeCode(inv._id), base);
      existingRows.push({
        rowNumber: row.rowNumber,
        code: String(inv._id),
        startDate: row.startDate,
        endDate: row.endDate,
        printingCharge: row.printingCharge,
        mountingCharge: row.mountingCharge,
        specialRatePerMonth: row.specialRatePerMonth,
        errors: [],
      });
      return;
    }

    // ---- new trading inventory ----
    out.kind = "new";
    const missing = [];
    if (!row.town) missing.push("Town");
    const vehicle = canonicalVehicle(row.mediaVehicle);
    if (!row.mediaVehicle) missing.push("Media Vehicle");
    else if (!vehicle) errors.push(`Media Vehicle "${row.mediaVehicle}" must be one of ${MEDIA_VEHICLES.join(", ")}`);
    if (!row.width) missing.push("Size (W)");
    if (!row.height) missing.push("Size (H)");
    if (!row.illu) missing.push("Media Type");
    else if (!ILLU_TYPES.includes(row.illu)) errors.push(`Media Type "${row.illu}" must be one of ${ILLU_TYPES.join(", ")}`);
    if (!row.ratePerMonth) missing.push("Rate PM");
    if (missing.length > 0) errors.push(`Needed to create a new inventory: ${missing.join(", ")}`);

    const span = daysBetween(row.startDate, row.endDate);
    if (span < 1) errors.push("End Date is before Start Date");
    else if (span > MAX_RANGE_DAYS) errors.push("Booking period is longer than 10 years");

    if (errors.length > 0) return;

    const groupKey = `${normalizeLocationKey(row.town)}|${normalizeLocationKey(row.location)}`;
    const group = groups.get(groupKey) || [];
    if (group.length > 0) {
      const first = group[0];
      if (!allowDuplicateInventories) {
        errors.push(`Duplicate of row ${first.rowNumber} in this file`);
        return;
      }
      const clash = group.find((g) => rangesOverlap(g.startDate, g.endDate, row.startDate, row.endDate));
      if (clash) {
        errors.push(`Dates overlap row ${clash.rowNumber} for the same inventory`);
        return;
      }
    }
    group.push({ rowNumber: row.rowNumber, startDate: row.startDate, endDate: row.endDate });
    groups.set(groupKey, group);

    out.groupKey = groupKey;
    out.newInv = {
      city: row.town,
      area: row.town,
      name: row.location,
      mediaVehicle: vehicle,
      illu: row.illu,
      width: row.width,
      height: row.height,
      ratePerMonth: row.ratePerMonth,
      purchaseRate: row.buyingRate,
    };
    out.vendor = "";
    out.suffix = DEFAULT_SUFFIX;
    out.sheet = {
      printingCharge: row.printingCharge,
      mountingCharge: row.mountingCharge,
    };

    // Preview figures for the table; the final line is built after creation.
    out.printingCharge = packageRate ? 0 : row.printingCharge;
    out.mountingCharge = packageRate ? 0 : row.mountingCharge;
    out.defaults = {
      printingCharge: !packageRate && row.printingCharge === null,
      mountingCharge: !packageRate && row.mountingCharge === null,
    };
    const monthly = packageRate ? 0 : row.specialRatePerMonth ?? row.ratePerMonth;
    out.specialRatePerMonth = monthly;
    const preview = calcSpecialRate(
      { specialRatePerMonth: monthly, rpm: row.ratePerMonth, ratePerMonth: row.ratePerMonth },
      row.startDate,
      row.endDate
    );
    out.amount = preview.newSpecialRate || 0;
    if (packageRate) out.warnings.push("Charges in the file are ignored because a package rate is set");
    out.status = "ready";
  });

  // Existing trading inventories: same rules as the Code No import.
  let existingPlan = { rows: [], lines: [] };
  if (existingRows.length > 0) {
    existingPlan = planBdfExcelImport({
      rows: existingRows,
      existingInventories,
      allowDuplicateInventories,
      packageRate,
      createLine,
      isDateBlocked,
      hasSiblingOverlap,
      calcSpecialRate,
      // The full/half pole rule is about Code Nos, which trading inventories may not have.
      canAddToCart: (items, cart) =>
        (cart || []).some((c) => !c) ? { canAdd: true, reason: "" } : canAddToCart(items, cart),
      resolveInventory: (code) => {
        const inventory = inventoryByCode.get(normalizeCode(code));
        return inventory ? { inventory } : null;
      },
    });
    existingPlan.rows.forEach((planned) => {
      const out = byRow.get(planned.rowNumber);
      out.errors.push(...planned.errors);
      out.warnings.push(...planned.warnings);
      out.printingCharge = planned.printingCharge;
      out.mountingCharge = planned.mountingCharge;
      out.specialRatePerMonth = planned.specialRatePerMonth;
      out.amount = planned.amount;
      out.defaults = planned.defaults;
      out.line = planned.line;
      out.location = out.location || planned.location;
      out.status = out.errors.length === 0 ? "ready" : "error";
    });
  }

  const ordered = rows.map((r) => byRow.get(r.rowNumber));
  const ready = ordered.filter((p) => p.status === "ready");
  return {
    rows: ordered,
    readyCount: ready.length,
    errorCount: ordered.length - ready.length,
    newCount: ready.filter((p) => p.kind === "new").length,
    existingCount: ready.filter((p) => p.kind === "existing").length,
  };
};

/**
 * Builds the BDF line for a new trading inventory once it exists.
 * `base` is the picker-shaped line made from the created inventory.
 */
export const buildNewTradingLine = ({ base, row, packageRate, calcSpecialRate, withKey }) => {
  const sheet = row.sheet || {};
  const pkg = Boolean(packageRate);
  const line = {
    ...base,
    printingCharge: pkg ? 0 : sheet.printingCharge ?? base.printingCharge,
    mountingCharge: pkg ? 0 : sheet.mountingCharge ?? base.mountingCharge,
    specialRatePerMonth: pkg ? 0 : base.specialRatePerMonth,
    startDate: row.startDate,
    endDate: row.endDate,
  };
  const { newSpecialRate, discount } = calcSpecialRate(line, row.startDate, row.endDate);
  line.specialRate = newSpecialRate || 0;
  line.discount = discount;
  return withKey ? withKey(line) : line;
};

/** Rows that still need something from the user before the import can run. */
export const getTradingRowProblems = (plan, edits) => {
  const problems = [];
  const seen = new Set();
  (plan?.rows || []).forEach((row) => {
    if (row.status !== "ready" || row.kind !== "new" || seen.has(row.groupKey)) return;
    seen.add(row.groupKey);
    const edit = (edits && edits[row.groupKey]) || {};
    const vendor = edit.vendor ?? row.vendor;
    const purchaseRate = edit.purchaseRate ?? row.newInv.purchaseRate;
    if (!vendor) problems.push({ groupKey: row.groupKey, rowNumber: row.rowNumber, field: "vendor" });
    if (!(Number(purchaseRate) > 0)) problems.push({ groupKey: row.groupKey, rowNumber: row.rowNumber, field: "purchaseRate" });
  });
  return problems;
};

/** Header-only template following the usual trading sheet. */
export const buildTradingImportTemplate = () => {
  const wb = XLSX.utils.book_new();
  const headers = TRADING_COLUMNS.map((c) => c.label);
  const sheet = XLSX.utils.aoa_to_sheet([headers]);
  sheet["!cols"] = headers.map((h) => ({ wch: Math.max(14, h.length + 4) }));
  XLSX.utils.book_append_sheet(wb, sheet, "Trading Inventories");
  const notes = XLSX.utils.aoa_to_sheet([
    ["How to fill the Trading Inventories sheet"],
    ["Location, Start Date and End Date are required in every row."],
    ["If the Location already exists as a trading inventory it is used as it is; otherwise a new trading inventory is created."],
    ["For a new inventory also fill: Town, Media Vehicle, Size (W), Size (H), Media Type (FL / BL / NL), Rate PM."],
    ["Media Vehicle: " + MEDIA_VEHICLES.join(", ") + "."],
    ["Buying Rate PM and the vendor can be filled in on the preview screen; suffix defaults to HO."],
    ["Only Rate PM (rate per month) and Buying Rate PM (buying cost per month) are needed; other rate columns are ignored."],
    ["Dates: use DD-MM-YYYY (for example 05-10-2026) or real Excel dates."],
    ["Column order does not matter."],
  ]);
  notes["!cols"] = [{ wch: 110 }];
  XLSX.utils.book_append_sheet(wb, notes, "Instructions");
  return XLSX.write(wb, { bookType: "xlsx", type: "array" });
};
